import { colors } from '@apollo/brand';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  customColors,
} from '@apollo/orbit';
// TODO(Rebrand): Migrate this to orbit's Select when implementing dark mode for Insights
import { Select } from '@apollo/space-kit/Select';
import classnames from 'classnames';
import React from 'react';

import { InsightsLineChartTooltip } from 'src/app/graph/insights/InsightsLineChartTooltip';
import {
  LineChart,
  LineChartStackConfig,
} from 'src/components/charts/LineChart';
import { Loading } from 'src/components/common/loading/Loading';
import { NoData } from 'src/components/common/noData/NoData';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { DateRangeBadge } from 'src/components/dateRangeBadge/DateRangeBadge';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { CoordinateKind, Resolution } from 'src/lib/graphqlTypes/types';

import { FieldRequest } from '../../useCurrentSchemaCoordinate';

export type FieldMetricType = 'requests' | 'executions';

const friendlyCoordinateKind = new Map<CoordinateKind, string>([
  [CoordinateKind.ENUM_VALUE, 'enum value'],
  [CoordinateKind.INPUT_OBJECT_FIELD, 'input field'],
  [CoordinateKind.OBJECT_FIELD, 'field'],
]);

interface Props {
  loading: boolean;
  requests: FieldRequest[];
  resolution: Resolution;
  showSubscriptionMissingInfo: boolean;
  showClientFiltersWarning: boolean;
  coordinateKind: CoordinateKind;
  __test?: {
    fieldMetricType?: FieldMetricType;
  };
}

export const FieldPageRequestsChart = ({
  loading,
  requests,
  resolution,
  showSubscriptionMissingInfo,
  showClientFiltersWarning,
  coordinateKind,
  __test,
}: Props) => {
  const hasRequestMetrics = requests?.some(
    ({ requestCount }) => requestCount > 0,
  );
  const hasExecutionMetrics = requests?.some(
    ({ estimatedExecutionCount }) => estimatedExecutionCount > 0,
  );

  const [fieldMetricType, setFieldMetricType] = React.useState<FieldMetricType>(
    __test?.fieldMetricType ?? 'requests',
  );
  const [isFieldMetricTypeDirty, setIsFieldMetricTypeDirty] =
    React.useState<Boolean>(false);

  const hideExecutions =
    coordinateKind === CoordinateKind.INPUT_OBJECT_FIELD ||
    coordinateKind === CoordinateKind.ENUM_VALUE;

  // Once our queries are idle default the field metric type dropdown depending
  // on the presence of requests and execution count. Once the user has manually
  // changed the dropdown we no longer set a 'smart' default value.
  React.useEffect(() => {
    if (!loading && !isFieldMetricTypeDirty) {
      if (
        fieldMetricType === 'requests' &&
        !hasRequestMetrics &&
        hasExecutionMetrics &&
        !hideExecutions
      ) {
        setFieldMetricType('executions');
      } else if (
        fieldMetricType === 'executions' &&
        hasRequestMetrics &&
        !hasExecutionMetrics
      ) {
        setFieldMetricType('requests');
      }
    }
  }, [
    fieldMetricType,
    hasExecutionMetrics,
    hasRequestMetrics,
    isFieldMetricTypeDirty,
    loading,
    hideExecutions,
  ]);

  const trackCustomEvent = useTrackCustomEvent();

  const stacks: LineChartStackConfig<FieldRequest>[] = [
    {
      colorGradientTop: `${colors.primitives.blue[300]}3C`,
      colorGradientBottom: `${colors.primitives.blue[300]}00`,
      primaryColor: customColors.flowchart.d,
      cursorColor: customColors.brand.accent,
      label: fieldMetricType === 'requests' ? 'Requests' : 'Field Executions',
      data: requests,
      getValue: ({
        timestamp,
        requestCount,
        estimatedExecutionCount,
      }: FieldRequest) => ({
        timestamp,
        value:
          fieldMetricType === 'requests'
            ? requestCount
            : estimatedExecutionCount,
      }),
    },
  ];

  const noRequests = Boolean(
    fieldMetricType === 'requests' ? !hasRequestMetrics : !hasExecutionMetrics,
  );

  return (
    <Card variant="unstyled">
      <CardHeader>
        <CardTitle>
          <>
            {hideExecutions ? (
              <span className="pl-3">Requests</span>
            ) : (
              <Select
                value={fieldMetricType}
                onChange={(event) => {
                  const newValue =
                    event.target.value === 'requests'
                      ? 'requests'
                      : 'executions';
                  trackCustomEvent({
                    action: 'user_action',
                    category: 'Field Insights',
                    label: `Change requests chart mode: ${newValue}`,
                  });
                  setFieldMetricType(newValue);
                  setIsFieldMetricTypeDirty(true);
                }}
                feel="flat"
                className="self-center font-heading text-xl font-medium text-heading"
                placement="bottom-start"
              >
                <option
                  className="font-normal"
                  value={'requests' as FieldMetricType}
                >
                  Requests
                </option>
                <option
                  className="font-normal"
                  value={'executions' as FieldMetricType}
                >
                  Field executions
                </option>
              </Select>
            )}
            <span className="self-center">
              <DateRangeBadge />
            </span>
            {(fieldMetricType === 'executions' &&
              showSubscriptionMissingInfo) ||
            showClientFiltersWarning ? (
              <Tooltip
                label={
                  <ul
                    className={classnames({
                      'list-inside list-disc':
                        showSubscriptionMissingInfo && showClientFiltersWarning,
                    })}
                  >
                    {showSubscriptionMissingInfo && (
                      <li data-testid="executions-graph-subscriptions-warning">
                        This chart does not yet include any field executions for
                        subscription operations.
                      </li>
                    )}
                    {showClientFiltersWarning && (
                      <li data-testid="executions-graph-clients-warning">
                        Showing data from all clients. This chart does not
                        currently reflect any client filters that have been
                        selected.
                      </li>
                    )}
                  </ul>
                }
              >
                <IconWarning
                  className="ml-2 self-center text-icon-warning"
                  data-testid="executions-graph-warning-trigger"
                />
              </Tooltip>
            ) : null}
          </>
        </CardTitle>
        <CardDescription className="pl-3">
          {fieldMetricType === 'requests'
            ? `The number of times an operation has requested this ${friendlyCoordinateKind.get(
                coordinateKind,
              )}.`
            : 'The number of times this field was executed.'}
        </CardDescription>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="flex justify-center py-10 text-center">
            <Loading />
          </div>
        ) : noRequests ? (
          <NoData
            className="flex h-56 items-center justify-center"
            message={`There are no ${
              fieldMetricType === 'requests' ? 'requests' : 'executions'
            } for this field in this time period.`}
          />
        ) : (
          <LineChart
            stacked
            stacks={stacks}
            className="h-56"
            resolution={resolution}
            allowSingleXAxisValue
            tooltipLabelAs={<InsightsLineChartTooltip />}
            hideCursorArrow
          />
        )}
      </CardBody>
    </Card>
  );
};
